/**
 * The number room: toys about counting and arithmetic, sharing one canvas
 * surface and switched by a tab row. They are ordered by how much number sense
 * they assume, so the same room works for a child counting blocks and for
 * someone taking 2431 apart.
 *
 * Toys about the STRUCTURE of numbers — sequences, primes, the wheel — live
 * next door in the pattern room, because ten tabs in here wrapped to three
 * rows on a phone and ate the toy above them.
 */

import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Blocks, Grid3x3, HandPlatter, Move3d, Rocket, Scissors } from 'lucide-preact';
import { Chip } from '../ui/controls.tsx';
import { NumberLab } from './math/NumberLab.tsx';
import { DivideToy } from './math/DivideToy.tsx';
import { PlaceValueToy } from './math/PlaceValueToy.tsx';
import { TimesToy } from './math/TimesToy.tsx';
import { BigNumberToy } from './math/BigNumberToy.tsx';
import { ShareToy } from './math/ShareToy.tsx';

type ToyId = 'count' | 'rows' | 'times' | 'share' | 'divide' | 'big';

const TOYS: Array<{ id: ToyId; label: string; icon: JSX.Element }> = [
  { id: 'count', label: 'Count', icon: <Blocks size={20} strokeWidth={2.75} /> },
  { id: 'rows', label: 'Rows', icon: <Grid3x3 size={20} strokeWidth={2.75} /> },
  { id: 'times', label: 'Times', icon: <Move3d size={20} strokeWidth={2.75} /> },
  { id: 'share', label: 'Share', icon: <HandPlatter size={20} strokeWidth={2.75} /> },
  { id: 'divide', label: 'Divide', icon: <Scissors size={20} strokeWidth={2.75} /> },
  { id: 'big', label: 'Big', icon: <Rocket size={20} strokeWidth={2.75} /> },
];

interface Props {
  onExit: () => void;
}

export function MathLab({ onExit }: Props): JSX.Element {
  const [toy, setToy] = useState<ToyId>('count');

  const tabs = (
    <div class="dock__row dock__row--tabs" role="group" aria-label="Number toys">
      {TOYS.map((t) => (
        <Chip key={t.id} label={t.label} pressed={toy === t.id} onClick={() => setToy(t.id)}>
          {t.icon}
        </Chip>
      ))}
    </div>
  );

  switch (toy) {
    case 'count':
      return <PlaceValueToy onExit={onExit} tabs={tabs} />;
    case 'rows':
      return <NumberLab onExit={onExit} tabs={tabs} />;
    case 'times':
      return <TimesToy onExit={onExit} tabs={tabs} />;
    case 'share':
      return <ShareToy onExit={onExit} tabs={tabs} />;
    case 'divide':
      return <DivideToy onExit={onExit} tabs={tabs} />;
    case 'big':
      return <BigNumberToy onExit={onExit} tabs={tabs} />;
  }
}
